import { useCallback } from "react";
import {
  MODE_CONFIGS,
  MAX_UNLOCK_LEVEL,
  BETA_UNLOCK_ALL,
} from "../state/modeConfig";
import { xpForLevel } from "../logic/scoring";
import { ModeCard } from "../components/ModeCard";
import { XPProgressBar } from "../components/XPProgressBar";
import { useCheatCode } from "../hooks/useCheatCode";
import { useTranslation } from "../i18n/useTranslation";
import { useTheme } from "../theme/useTheme";
import type { ThemePreference } from "../theme/ThemeProvider";
import type { Locale } from "../i18n/types";
import type { usePlayerState } from "../hooks/usePlayerState";

interface MainMenuProps {
  user: string;
  player: ReturnType<typeof usePlayerState>;
  onStartMode: (modeId: string) => void;
}

export function MainMenu({ user, player, onStartMode }: MainMenuProps) {
  const { state, setTotalXp, reset } = player;
  const { t, locale, setLocale } = useTranslation();
  const { preference: themePref, setPreference: setTheme } = useTheme();

  const onCheatFire = useCallback(() => {
    const target = xpForLevel(MAX_UNLOCK_LEVEL);
    if (state.totalXp < target) setTotalXp(target);
  }, [state, setTotalXp]);
  const handleCheatTap = useCheatCode(onCheatFire);

  const handleReset = () => {
    const ok = window.confirm(t("menu.resetConfirm"));
    if (ok) reset();
  };

  const xpInCurrentLevel = state.totalXp - state.xpForCurrentLevel;
  const xpForLevelSpan = state.xpForNextLevel - state.xpForCurrentLevel;
  const xpRemaining = Math.max(0, state.xpForNextLevel - state.totalXp);

  return (
    <main className="min-h-screen px-5 py-8 max-w-2xl mx-auto">
      {/* Brand */}
      <header className="mb-6 flex items-start justify-between gap-3">
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-fg-2 leading-relaxed">
          {t("menu.header", { user })}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <ThemeToggle preference={themePref} onChange={setTheme} t={t} />
          <LocaleToggle locale={locale} onChange={setLocale} t={t} />
        </div>
      </header>

      {/* Level + XP block */}
      <section className="mb-8">
        <div className="flex items-end justify-between mb-2">
          <button
            type="button"
            onClick={handleCheatTap}
            className="text-3xl font-bold tracking-[-0.02em] text-fg-0 select-none focus:outline-none"
            aria-label={`${t("results.level")} ${state.level}`}
          >
            {t("results.level")} {state.level}
            <span className="text-fg-2 text-lg font-normal"> {t("menu.levelOfFifty")}</span>
          </button>
          <div className="text-right">
            <div className="text-cyan text-sm font-medium">
              {state.totalXp.toLocaleString()} {t("menu.xpSuffix")}
            </div>
            <div className="text-fg-3 text-[10px] mt-0.5">
              {t("menu.nextXp", { amount: xpRemaining.toLocaleString() })}
            </div>
          </div>
        </div>

        <XPProgressBar
          fraction={xpForLevelSpan > 0 ? xpInCurrentLevel / xpForLevelSpan : 0}
        />

        <div className="text-fg-3 text-xs mt-2">
          {t("menu.sessions", { count: state.sessionsPlayed })}
        </div>
      </section>

      {/* Mode list */}
      <section className="flex flex-col gap-2 mb-12">
        {MODE_CONFIGS.map((config, i) => (
          <ModeCard
            key={config.modeId}
            config={config}
            isUnlocked={BETA_UNLOCK_ALL || state.isUnlocked(config.modeId)}
            highScore={state.modeHighScores[config.modeId] ?? 0}
            difficulty={state.getModeDifficulty(config.modeId)}
            onClick={() => onStartMode(config.modeId)}
            index={i + 1}
          />
        ))}
      </section>

      {/* Footer — reset + beta tip */}
      <footer className="border-t border-[var(--color-line)] pt-6 flex flex-col gap-3">
        <button
          type="button"
          onClick={handleReset}
          className="self-start font-mono text-[11px] tracking-[0.06em] text-fg-3 hover:text-red transition-colors"
        >
          {t("menu.reset")}
        </button>
        {BETA_UNLOCK_ALL && (
          <p className="text-[11px] font-mono tracking-[0.04em] text-amber">
            {t("menu.betaNotice")}
          </p>
        )}
        <p className="text-[11px] text-fg-4 leading-relaxed">
          {t("menu.savesNotice")}
        </p>
      </footer>
    </main>
  );
}

interface LocaleToggleProps {
  locale: Locale;
  onChange: (next: Locale) => void;
  t: (key: string, args?: Record<string, unknown>) => string;
}

function LocaleToggle({ locale, onChange, t }: LocaleToggleProps) {
  const btn = (target: Locale) => {
    const active = locale === target;
    return (
      <button
        key={target}
        type="button"
        onClick={() => onChange(target)}
        aria-pressed={active}
        aria-label={t("locale.label") + ": " + t(`locale.${target}`)}
        className={[
          "px-2.5 py-1 font-mono text-[10px] tracking-[0.06em] rounded-md transition-colors",
          active
            ? "bg-fg-0 text-bg-1"
            : "text-fg-3 hover:text-fg-0",
        ].join(" ")}
      >
        {t(`locale.${target}`)}
      </button>
    );
  };
  return (
    <div className="flex items-center gap-1 bg-bg-2 rounded-lg p-0.5 border border-[var(--color-line)]">
      {btn("en")}
      {btn("es")}
    </div>
  );
}

interface ThemeToggleProps {
  preference: ThemePreference;
  onChange: (next: ThemePreference) => void;
  t: (key: string, args?: Record<string, unknown>) => string;
}

function ThemeToggle({ preference, onChange, t }: ThemeToggleProps) {
  const icon: Record<ThemePreference, string> = {
    light: "☀",
    dark: "☾",
  };
  const btn = (target: ThemePreference) => {
    const active = preference === target;
    return (
      <button
        key={target}
        type="button"
        onClick={() => onChange(target)}
        aria-pressed={active}
        aria-label={`${t("theme.label")}: ${t(`theme.${target}`)}`}
        title={t(`theme.${target}`)}
        className={[
          "px-2 py-1 font-mono text-xs rounded-md transition-colors leading-none",
          active ? "bg-fg-0 text-bg-1" : "text-fg-3 hover:text-fg-0",
        ].join(" ")}
      >
        {icon[target]}
      </button>
    );
  };
  return (
    <div className="flex items-center gap-1 bg-bg-2 rounded-lg p-0.5 border border-[var(--color-line)]">
      {btn("light")}
      {btn("dark")}
    </div>
  );
}
